import { Routes, Route, useLocation } from "react-router-dom";
import { useState, lazy, Suspense, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Helmet } from "react-helmet-async";

import Header from "./component/Header/Header.jsx";
import Hero from "./component/sections/Hero.jsx";

// ✅ SEO Head Component
import SEOHead from "./components/SEO/SEOHead.jsx";
import SchemaMarkup from "./components/SEO/SchemaMarkup.jsx";
import seoConfig from "./config/seoConfig.js";
import {
  organizationSchema,
  homePageSchema,
  softwareApplicationSchema,
  featureDetailSchema,
  featureSchemas,
  breadcrumbSchema,
  contactPageSchema,
  aboutPageSchema,
  speakableSchema,
  reviewSchema,
  pricingPageSchema,
  faqSchema,
} from "./config/schemaMarkup.js";
import { PLAN_PRICE_LIST, PLAN_DIFFERENCES_TEXT, planOffersSchema } from "./config/pricing.js";

// ✅ Non-critical components — lazy-loaded out of the main bundle to cut TBT
const BookDemoButton = lazy(() => import("./component/sections/Stats.jsx"));
const ServiceWorkerRegistration = lazy(() => import("./components/ServiceWorkerRegistration"));
const PerformanceMonitoring = lazy(() => import("./components/PerformanceMonitoring"));
const GoogleAnalytics = lazy(() => import("./components/Analytics/GoogleAnalytics.jsx"));
const MetaPixel = lazy(() => import("./components/Analytics/MetaPixel.jsx"));
const GTM = lazy(() => import("./components/Analytics/GTM.jsx"));

const Footer = lazy(() => import("./component/Footer/Footer.jsx"));
const Contact = lazy(() => import("./component/sections/Contact.jsx"));
const FAQ = lazy(() => import("./component/softwareDetails.jsx/FAQ.jsx"));
const SocialProof = lazy(() => import("./component/sections/SocialProof.jsx"));
const FeatureShowcase = lazy(() => import("./component/sections/FeatureShowcase.jsx"));
const IndustryGrid = lazy(() => import("./component/sections/IndustryGrid.jsx"));
const HowItWorks = lazy(() => import("./component/sections/HowItWorks.jsx"));
const PricingPage = lazy(() => import("./component/Pricing/PricingPage.jsx"));
const AboutUs = lazy(() => import("./component/About/AboutUs.jsx"));
const TryForFreeModal = lazy(() => import("./component/TryForFreeModal.jsx"));
const FeaturePage = lazy(() => import("./component/Feature/FeaturePage.jsx"));
const CommonModal = lazy(() => import("./component/Modal/CommonModal.jsx"));
const ContactModalContent = lazy(() => import("./component/Feature/ContactModalContent"));
const DemoVideoModal = lazy(() => import("./component/Modal/DemoVideoModal.jsx"));
const PrivacyPolicy = lazy(() => import("./component/PrivacyPolicy/PrivacyPolicy.jsx"));
const NotFound = lazy(() => import("./component/NotFound/NotFound.jsx"));
const SalonPublicPage = lazy(() => import("./pages/partner/SalonPublicPage.jsx"));
const EmbedBookingPage = lazy(() => import("./pages/partner/EmbedBookingPage.jsx"));
// Google Ads landing pages — standalone, no site header/footer menus
const GrowRevenueLanding = lazy(() => import("./pages/landing/GrowRevenueLanding.jsx"));
const ThankYouLanding = lazy(() => import("./pages/landing/ThankYouLanding.jsx"));
const SoftwareOnlyLanding = lazy(() => import("./pages/landing/SoftwareOnlyLanding.jsx"));

// ✅ Lazy-loaded Content Pages (Phase 5)
const ComparisonVsExcel = lazy(() => import("./pages/content/ComparisonVsExcel.jsx"));
const ComparisonVsPaper = lazy(() => import("./pages/content/ComparisonVsPaper.jsx"));
const CompetitorComparison = lazy(() => import("./pages/content/CompetitorComparison.jsx"));
const SuccessStory = lazy(() => import("./pages/content/SuccessStory.jsx"));

// ✅ Lazy-loaded How-To Guides (Phase 5 Week 2)
const GuidesHub = lazy(() => import("./pages/content/Guides/GuidesHub.jsx"));

// ✅ Lazy-loaded Phase 5 Week 3 Content (Testimonials, ROI, Use Cases)
const TestimonialsHub = lazy(() => import("./pages/content/TestimonialsHub.jsx"));
const ROICalculator = lazy(() => import("./pages/content/ROICalculator.jsx"));
const MobileApp = lazy(() => import("./pages/MobileApp.jsx"));
const UseCasesHub = lazy(() => import("./pages/content/UseCasesHub.jsx"));

// ✅ Lazy-loaded Phase 6 Session 1 - Detailed Use-Case Pages
const SoloStylist = lazy(() => import("./pages/usecases/SoloStylist.jsx"));
const SmallTeam = lazy(() => import("./pages/usecases/SmallTeam.jsx"));
const MediumSalon = lazy(() => import("./pages/usecases/MediumSalon.jsx"));
const MultiLocation = lazy(() => import("./pages/usecases/MultiLocation.jsx"));
const SpaWellness = lazy(() => import("./pages/usecases/SpaWellness.jsx"));

// ✅ Lazy-loaded Phase 6 Session 2 - Video Testimonials & Analytics
const VideoTestimonials = lazy(() => import("./pages/VideoTestimonials.jsx"));
const AnalyticsDashboard = lazy(() => import("./components/Analytics/AnalyticsDashboard.jsx"));

// ✅ Lazy-loaded Phase 6 Session 3 - Lead Management
const LeadManagementDashboard = lazy(() => import("./components/LeadManagement/LeadManagementDashboard.jsx"));

const Setup5StepGuide = lazy(() => import("./pages/content/Guides/Setup5StepGuide.jsx"));
const ReduceNoShowsGuide = lazy(() => import("./pages/content/Guides/ReduceNoShowsGuide.jsx"));
const Save40HoursGuide = lazy(() => import("./pages/content/Guides/Save40HoursGuide.jsx"));
const CommissionSystemGuide = lazy(() => import("./pages/content/Guides/CommissionSystemGuide.jsx"));
const MobileBookingOptimizationGuide = lazy(() => import("./pages/content/Guides/MobileBookingOptimizationGuide.jsx"));
const InventoryTrackingGuide = lazy(() => import("./pages/content/Guides/InventoryTrackingGuide.jsx"));
const WhatsAppMarketingGuide = lazy(() => import("./pages/content/Guides/WhatsAppMarketingGuide.jsx"));
const PricingStrategyGuide = lazy(() => import("./pages/content/Guides/PricingStrategyGuide.jsx"));

// ✅ Feature deep-dive animated videos
const FeatureVideos = lazy(() => import("./pages/FeatureVideos.jsx"));

// ✅ Lazy-loaded Blog Pages (Phase 5.4)
const Blog = lazy(() => import("./pages/Blog.jsx"));
const BlogArticle = lazy(() => import("./pages/BlogArticle.jsx"));

// ✅ Lazy-loaded City Landing Pages (Phase 5.5 - GEO Targeting)
const BangalorePage = lazy(() => import("./pages/citypages/Bangalore.jsx"));
const DelhiPage = lazy(() => import("./pages/citypages/Delhi.jsx"));
const HyderabadPage = lazy(() => import("./pages/citypages/Hyderabad.jsx"));
const ChennaiPage = lazy(() => import("./pages/citypages/Chennai.jsx"));
const PunePage = lazy(() => import("./pages/citypages/Pune.jsx"));
const KolkataPage = lazy(() => import("./pages/citypages/Kolkata.jsx"));
const AhmedabadPage = lazy(() => import("./pages/citypages/Ahmedabad.jsx"));
const MumbaiPage = lazy(() => import("./pages/citypages/Mumbai.jsx"));

// ✅ Lazy-loaded Feature Pages
const AppointmentScheduling = lazy(
  () => import("./pages/features/AppointmentScheduling"),
);
const StaffManagement = lazy(() => import("./pages/features/StaffManagement"));
const ClientsManagement = lazy(
  () => import("./pages/features/ClientsManagement"),
);
const InventoryManagement = lazy(
  () => import("./pages/features/InventoryManagement"),
);
const DiscountOffers = lazy(() => import("./pages/features/DiscountOffers"));
const ClientAnalytics = lazy(() => import("./pages/features/ClientAnalytics"));
const InvoiceSystem = lazy(() => import("./pages/features/InvoiceSystem"));
const MembershipManagement = lazy(
  () => import("./pages/features/MembershipManagement"),
);
const GSTAccounting = lazy(() => import("./pages/features/GSTAccounting"));
const LoyaltyRewards = lazy(() => import("./pages/features/LoyaltyRewards"));
const MultiBranchManagement = lazy(() => import("./pages/features/MultiBranchManagement"));
const StaffAttendancePayroll = lazy(() => import("./pages/features/StaffAttendancePayroll"));
const ExpenseManagement = lazy(() => import("./pages/features/ExpenseManagement"));

const homepageReviews = [
  reviewSchema("Priya Sharma", "5", "Bookings went up 35% in our first month. The automated reminders alone cut no-shows in half."),
  reviewSchema("Rahul Mehta", "5", "Staff scheduling used to take hours every week. Now it's done in minutes. Outstanding tool."),
  reviewSchema("Ananya Reddy", "5", "Our membership revenue doubled after switching. The platform practically runs itself."),
];

const homeFaqs = [
  { question: "Can I see a demo before I buy?", answer: "Yes — book a free, personalized live demo and our team will walk you through every feature using your salon's real workflow. No credit card required, and we'll help you get fully set up from day one." },
  { question: "How quickly can I set up my salon?", answer: "Most salons are fully live in under 30 minutes. Add your services, invite your staff, and you're ready to accept online bookings. Our onboarding team personally helps you get started." },
  { question: "Can clients book without downloading an app?", answer: "Yes. Clients book directly from your website, Google listing, WhatsApp, Instagram, or a shareable link — no app download needed on their end." },
  { question: "How do automated reminders work?", answer: "AyeSalon automatically sends SMS and WhatsApp reminders to clients 24 hours and 1 hour before their appointment. This alone reduces no-shows by up to 40% on average." },
  { question: "Can I manage multiple branches?", answer: "Yes. AyeSalon supports unlimited branches under one account with centralised staff scheduling, inventory management, and revenue reporting across all locations." },
  { question: "What payment modes does AyeSalon support?", answer: "Cash, UPI (Google Pay, PhonePe, Paytm), debit/credit cards, partial payments, and wallet payments from membership balance are all supported." },
  { question: "Is my data secure?", answer: "Yes. All data is encrypted in transit and at rest, hosted on AWS infrastructure, with daily backups and access controls to keep your client information safe." },
  { question: "Can I run promotions and campaigns?", answer: "Absolutely. Send bulk SMS and WhatsApp promotions to your entire client list or targeted segments. Create discount codes, membership deals, and referral offers — all from one place." },
];

const pricingFaqs = [
  { question: "How much does AyeSalon cost?", answer: `AyeSalon has three annual plans: ${PLAN_PRICE_LIST}. Every plan starts with a free, personalised demo — no credit card required.` },
  { question: "Can I try AyeSalon before I buy?", answer: "Yes. Book a free, personalised live demo and our team will walk you through every feature using your salon's real workflow. No credit card required." },
  { question: "What is the difference between Basic, Regular, and Premium plans?", answer: PLAN_DIFFERENCES_TEXT },
  { question: "Can I switch plans later?", answer: "Yes. You can upgrade or downgrade your plan at any time. Changes take effect from the next billing cycle." },
  { question: "Do you offer discounts for multi-location salons?", answer: "Yes. Contact our sales team at sales@ayesalon.com or call +91-7090238960 for custom pricing for salon chains and multi-location businesses." },
];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
    }}
  >
    <CircularProgress />
  </Box>
);

export default function App() {
  const [openDemo, setOpenDemo] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const [openDemoVideo, setOpenDemoVideo] = useState(false);
  const { pathname } = useLocation();
  const isPartnerPage = pathname.startsWith("/partner/");
  const isEmbedPage = pathname.startsWith("/embed/");
  const isAdLandingPage = pathname.startsWith("/lp/");

  useEffect(() => {
    const handler = () => setOpenDemo(true);
    window.addEventListener("open-demo-modal", handler);
    return () => window.removeEventListener("open-demo-modal", handler);
  }, []);

  return (
    <>
      {/* ✅ Non-critical singletons — each splits into its own chunk */}
      <Suspense fallback={null}><ServiceWorkerRegistration /></Suspense>
      <Suspense fallback={null}><PerformanceMonitoring /></Suspense>
      <Suspense fallback={null}><GoogleAnalytics /></Suspense>
      <Suspense fallback={null}><MetaPixel /></Suspense>
      <Suspense fallback={null}><GTM /></Suspense>

      {/* Skip to main content link for keyboard navigation */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          top: "-100px",
          left: 0,
          background: "#9e4562",
          color: "#fff",
          padding: "8px 16px",
          fontWeight: 600,
          borderRadius: "0 0 8px 0",
          textDecoration: "none",
          zIndex: 10000,
          transition: "top 0.15s ease",
          "&:focus": {
            top: 0,
          },
        }}
      >
        Skip to main content
      </Box>

      <ScrollToTop />
      {!isPartnerPage && !isEmbedPage && !isAdLandingPage && <Suspense fallback={null}><BookDemoButton onClick={() => setOpenDemo(true)} /></Suspense>}

      {/* Global Book-a-Demo modal — mounted outside Routes so the floating
          BookDemoButton works on every page, not just home/about. */}
      {openDemo && (
        <Suspense fallback={null}>
          <TryForFreeModal
            open={openDemo}
            onClose={() => setOpenDemo(false)}
          />
        </Suspense>
      )}

      <Routes>
        {/* HOME */}
        <Route
          path="/"
          element={
            <>
              <SEOHead
                title={seoConfig.home.title}
                description={seoConfig.home.description}
                canonical={seoConfig.home.canonical}
                ogImage={seoConfig.home.ogImage}
                keywords={seoConfig.home.keywords}
              />
              <SchemaMarkup schemas={[organizationSchema, homePageSchema, softwareApplicationSchema, faqSchema(homeFaqs), speakableSchema(["h1", "main"]), ...homepageReviews]} />
              <Header />
              <main id="main-content">
                <Hero
                  onGetStarted={() => setOpenDemo(true)}
                  onViewDemo={() => setOpenDemoVideo(true)}
                />
                {/* <BookDemoButton onClick={() => setOpenDemo(true)} /> */}
                <Suspense fallback={null}>
                  <SocialProof />
                  <FeatureShowcase />
                  <IndustryGrid />
                  <HowItWorks />
                  <FAQ />
                  <Contact />
                </Suspense>
              </main>
              <Suspense fallback={null}>
                <Footer />
              </Suspense>

              {openDemoVideo && (
                <Suspense fallback={null}>
                  <DemoVideoModal
                    open={openDemoVideo}
                    onClose={() => setOpenDemoVideo(false)}
                  />
                </Suspense>
              )}
            </>
          }
        />

        {/* FEATURE PAGE */}
        <Route
          path="/features"
          element={
            <>
              <SEOHead
                title={seoConfig.features.title}
                description={seoConfig.features.description}
                canonical={seoConfig.features.canonical}
                ogImage={seoConfig.features.ogImage}
                keywords={seoConfig.features.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  breadcrumbSchema("/features", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                  ]),
                ]}
              />
              <Header />
              <main id="main-content">
                <Suspense fallback={<LoadingFallback />}>
                  <FeaturePage onViewDemo={() => setOpenContact(true)} />
                </Suspense>
              </main>
              <Suspense fallback={null}>
                <Footer />
              </Suspense>

              {/* ✅ New Reusable Modal */}
              {openContact && (
                <Suspense fallback={null}>
                  <CommonModal
                    open={openContact}
                    onClose={() => setOpenContact(false)}
                    width={1200}
                  >
                    <ContactModalContent />
                  </CommonModal>
                </Suspense>
              )}
            </>
          }
        />

        {/* PRICING */}
        <Route
          path="/pricing"
          element={
            <>
              <SEOHead
                title={seoConfig.pricing.title}
                description={seoConfig.pricing.description}
                canonical={seoConfig.pricing.canonical}
                ogImage={seoConfig.pricing.ogImage}
                keywords={seoConfig.pricing.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  breadcrumbSchema("/pricing", [
                    { name: "Home", url: "/" },
                    { name: "Pricing", url: "/pricing" },
                  ]),
                  pricingPageSchema(planOffersSchema),
                  faqSchema(pricingFaqs),
                ]}
              />
              <Header />
              <main id="main-content">
                <Suspense fallback={<LoadingFallback />}>
                  <PricingPage />
                </Suspense>
              </main>
              <Suspense fallback={null}>
                <Footer />
              </Suspense>
            </>
          }
        />

        {/* CONTACT PAGE */}
        <Route
          path="/contact"
          element={
            <>
              <SEOHead
                title={seoConfig.contact.title}
                description={seoConfig.contact.description}
                canonical={seoConfig.contact.canonical}
                ogImage={seoConfig.contact.ogImage}
                keywords={seoConfig.contact.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  contactPageSchema,
                  breadcrumbSchema("/contact", [
                    { name: "Home", url: "/" },
                    { name: "Contact", url: "/contact" },
                  ]),
                ]}
              />
              <Header />
              <main id="main-content">
                <Suspense fallback={<LoadingFallback />}>
                  <Contact isPage />
                </Suspense>
              </main>
              <Suspense fallback={null}>
                <Footer />
              </Suspense>
            </>
          }
        />

        <Route
          path="/privacyPolicy"
          element={
            <>
              <SEOHead
                title={seoConfig.privacyPolicy.title}
                description={seoConfig.privacyPolicy.description}
                canonical={seoConfig.privacyPolicy.canonical}
                ogImage={seoConfig.privacyPolicy.ogImage}
                keywords={seoConfig.privacyPolicy.keywords}
              />
              <SchemaMarkup schemas={[organizationSchema]} />
              <Header />
              <main id="main-content">
                <Suspense fallback={<LoadingFallback />}>
                  <PrivacyPolicy />
                </Suspense>
              </main>
              {/* <Footer /> */}
            </>
          }
        />

        {/* ABOUT */}
        <Route
          path="/about"
          element={
            <>
              <SEOHead
                title={seoConfig.about.title}
                description={seoConfig.about.description}
                canonical={seoConfig.about.canonical}
                ogImage={seoConfig.about.ogImage}
                keywords={seoConfig.about.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  aboutPageSchema,
                  breadcrumbSchema("/about", [
                    { name: "Home", url: "/" },
                    { name: "About", url: "/about" },
                  ]),
                ]}
              />
              <Header />
              <main id="main-content">
                <Suspense fallback={<LoadingFallback />}>
                  <AboutUs />
                </Suspense>
              </main>
              {/* <BookDemoButton onClick={() => setOpenDemo(true)} /> */}
              <Suspense fallback={null}>
                <Footer />
              </Suspense>
            </>
          }
        />

        {/* Feature Detail Routes */}
        <Route
          path="/features/appointment-scheduling"
          element={
            <>
              <SEOHead
                title={seoConfig.appointmentScheduling.title}
                description={seoConfig.appointmentScheduling.description}
                canonical={seoConfig.appointmentScheduling.canonical}
                ogImage={seoConfig.appointmentScheduling.ogImage}
                keywords={seoConfig.appointmentScheduling.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.appointmentScheduling.name,
                    featureSchemas.appointmentScheduling.description,
                    "Smart appointment scheduling for salons",
                    "/features/appointment-scheduling/"
                  ),
                  breadcrumbSchema("/features/appointment-scheduling", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Appointment Scheduling", url: "/features/appointment-scheduling" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <AppointmentScheduling />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/staff-management"
          element={
            <>
              <SEOHead
                title={seoConfig.staffManagement.title}
                description={seoConfig.staffManagement.description}
                canonical={seoConfig.staffManagement.canonical}
                ogImage={seoConfig.staffManagement.ogImage}
                keywords={seoConfig.staffManagement.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.staffManagement.name,
                    featureSchemas.staffManagement.description,
                    "Staff scheduling and management system",
                    "/features/staff-management/"
                  ),
                  breadcrumbSchema("/features/staff-management", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Staff Management", url: "/features/staff-management" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <StaffManagement />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/clients-management"
          element={
            <>
              <SEOHead
                title={seoConfig.clientsManagement.title}
                description={seoConfig.clientsManagement.description}
                canonical={seoConfig.clientsManagement.canonical}
                ogImage={seoConfig.clientsManagement.ogImage}
                keywords={seoConfig.clientsManagement.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.clientsManagement.name,
                    featureSchemas.clientsManagement.description,
                    "Complete client management system",
                    "/features/clients-management/"
                  ),
                  breadcrumbSchema("/features/clients-management", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Clients Management", url: "/features/clients-management" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <ClientsManagement />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/inventory-management"
          element={
            <>
              <SEOHead
                title={seoConfig.inventoryManagement.title}
                description={seoConfig.inventoryManagement.description}
                canonical={seoConfig.inventoryManagement.canonical}
                ogImage={seoConfig.inventoryManagement.ogImage}
                keywords={seoConfig.inventoryManagement.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.inventoryManagement.name,
                    featureSchemas.inventoryManagement.description,
                    "Inventory management and tracking",
                    "/features/inventory-management/"
                  ),
                  breadcrumbSchema("/features/inventory-management", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Inventory Management", url: "/features/inventory-management" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <InventoryManagement />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/discount-offers"
          element={
            <>
              <SEOHead
                title={seoConfig.discountOffers.title}
                description={seoConfig.discountOffers.description}
                canonical={seoConfig.discountOffers.canonical}
                ogImage={seoConfig.discountOffers.ogImage}
                keywords={seoConfig.discountOffers.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.discountOffers.name,
                    featureSchemas.discountOffers.description,
                    "Discount and offers management",
                    "/features/discount-offers/"
                  ),
                  breadcrumbSchema("/features/discount-offers", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Discount & Offers", url: "/features/discount-offers" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <DiscountOffers />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/client-analytics"
          element={
            <>
              <SEOHead
                title={seoConfig.clientAnalytics.title}
                description={seoConfig.clientAnalytics.description}
                canonical={seoConfig.clientAnalytics.canonical}
                ogImage={seoConfig.clientAnalytics.ogImage}
                keywords={seoConfig.clientAnalytics.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.clientAnalytics.name,
                    featureSchemas.clientAnalytics.description,
                    "Salon reports, client analytics, and business insights",
                    "/features/client-analytics/"
                  ),
                  breadcrumbSchema("/features/client-analytics", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Client Analytics", url: "/features/client-analytics" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <ClientAnalytics />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/invoice-system"
          element={
            <>
              <SEOHead
                title={seoConfig.invoiceSystem.title}
                description={seoConfig.invoiceSystem.description}
                canonical={seoConfig.invoiceSystem.canonical}
                ogImage={seoConfig.invoiceSystem.ogImage}
                keywords={seoConfig.invoiceSystem.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.invoiceSystem.name,
                    featureSchemas.invoiceSystem.description,
                    "Invoice, billing, and split payment system",
                    "/features/invoice-system/"
                  ),
                  breadcrumbSchema("/features/invoice-system", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Invoice System", url: "/features/invoice-system" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <InvoiceSystem />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/membership-management"
          element={
            <>
              <SEOHead
                title={seoConfig.membershipManagement.title}
                description={seoConfig.membershipManagement.description}
                canonical={seoConfig.membershipManagement.canonical}
                ogImage={seoConfig.membershipManagement.ogImage}
                keywords={seoConfig.membershipManagement.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.membershipManagement.name,
                    featureSchemas.membershipManagement.description,
                    "Membership program management",
                    "/features/membership-management/"
                  ),
                  breadcrumbSchema("/features/membership-management", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Membership Management", url: "/features/membership-management" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <MembershipManagement />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/gst-accounting"
          element={
            <>
              <SEOHead
                title={seoConfig.gstAccounting.title}
                description={seoConfig.gstAccounting.description}
                canonical={seoConfig.gstAccounting.canonical}
                ogImage={seoConfig.gstAccounting.ogImage}
                keywords={seoConfig.gstAccounting.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.gstAccounting.name,
                    featureSchemas.gstAccounting.description,
                    "GST accounting and tax compliance software for salons",
                    "/features/gst-accounting/"
                  ),
                  breadcrumbSchema("/features/gst-accounting", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "GST Accounting", url: "/features/gst-accounting" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <GSTAccounting />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/loyalty-rewards"
          element={
            <>
              <SEOHead
                title={seoConfig.loyaltyRewards.title}
                description={seoConfig.loyaltyRewards.description}
                canonical={seoConfig.loyaltyRewards.canonical}
                ogImage={seoConfig.loyaltyRewards.ogImage}
                keywords={seoConfig.loyaltyRewards.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.loyaltyRewards.name,
                    featureSchemas.loyaltyRewards.description,
                    "Loyalty and rewards program software for salons",
                    "/features/loyalty-rewards/"
                  ),
                  breadcrumbSchema("/features/loyalty-rewards", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Loyalty & Rewards", url: "/features/loyalty-rewards" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <LoyaltyRewards />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/multi-branch-management"
          element={
            <>
              <SEOHead
                title={seoConfig.multiBranchManagement.title}
                description={seoConfig.multiBranchManagement.description}
                canonical={seoConfig.multiBranchManagement.canonical}
                ogImage={seoConfig.multiBranchManagement.ogImage}
                keywords={seoConfig.multiBranchManagement.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.multiBranchManagement.name,
                    featureSchemas.multiBranchManagement.description,
                    "Multi-branch salon management software for salon chains",
                    "/features/multi-branch-management/"
                  ),
                  breadcrumbSchema("/features/multi-branch-management", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Multi-Branch Management", url: "/features/multi-branch-management" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <MultiBranchManagement />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/staff-attendance-payroll"
          element={
            <>
              <SEOHead
                title={seoConfig.staffAttendancePayroll.title}
                description={seoConfig.staffAttendancePayroll.description}
                canonical={seoConfig.staffAttendancePayroll.canonical}
                ogImage={seoConfig.staffAttendancePayroll.ogImage}
                keywords={seoConfig.staffAttendancePayroll.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.staffAttendancePayroll.name,
                    featureSchemas.staffAttendancePayroll.description,
                    "Staff attendance tracking and payroll software for salons",
                    "/features/staff-attendance-payroll/"
                  ),
                  breadcrumbSchema("/features/staff-attendance-payroll", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Staff Attendance & Payroll", url: "/features/staff-attendance-payroll" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <StaffAttendancePayroll />
              </Suspense>
            </>
          }
        />

        <Route
          path="/features/expense-management"
          element={
            <>
              <SEOHead
                title={seoConfig.expenseManagement.title}
                description={seoConfig.expenseManagement.description}
                canonical={seoConfig.expenseManagement.canonical}
                ogImage={seoConfig.expenseManagement.ogImage}
                keywords={seoConfig.expenseManagement.keywords}
              />
              <SchemaMarkup
                schemas={[
                  organizationSchema,
                  featureDetailSchema(
                    featureSchemas.expenseManagement.name,
                    featureSchemas.expenseManagement.description,
                    "Expense tracking and management software for salons",
                    "/features/expense-management/"
                  ),
                  breadcrumbSchema("/features/expense-management", [
                    { name: "Home", url: "/" },
                    { name: "Features", url: "/features" },
                    { name: "Expense Management", url: "/features/expense-management" },
                  ]),
                ]}
              />
              <Suspense fallback={<LoadingFallback />}>
                <ExpenseManagement />
              </Suspense>
            </>
          }
        />

        {/* ✅ Content Pages - Phase 5 */}
        <Route
          path="/comparison/vs-excel"
          element={
            <>
              <SEOHead
                title={seoConfig["comparison-vs-excel"]?.title}
                description={seoConfig["comparison-vs-excel"]?.description}
                canonical={seoConfig["comparison-vs-excel"]?.canonical}
                ogImage={seoConfig["comparison-vs-excel"]?.ogImage}
                keywords={seoConfig["comparison-vs-excel"]?.keywords}
              />
              <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/comparison/vs-excel", [
                { name: "Home", url: "/" },
                { name: "AyeSalon vs Excel", url: "/comparison/vs-excel" },
              ])]} />
              <Header />
              <main id="main-content">
                <Suspense fallback={<LoadingFallback />}>
                  <ComparisonVsExcel />
                </Suspense>
              </main>
              <Footer />
            </>
          }
        />

        <Route
          path="/comparison/vs-paper"
          element={
            <>
              <SEOHead
                title={seoConfig["comparison-vs-paper"]?.title}
                description={seoConfig["comparison-vs-paper"]?.description}
                canonical={seoConfig["comparison-vs-paper"]?.canonical}
                ogImage={seoConfig["comparison-vs-paper"]?.ogImage}
                keywords={seoConfig["comparison-vs-paper"]?.keywords}
              />
              <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/comparison/vs-paper", [
                { name: "Home", url: "/" },
                { name: "AyeSalon vs Paper", url: "/comparison/vs-paper" },
              ])]} />
              <Suspense fallback={<LoadingFallback />}>
                <ComparisonVsPaper />
              </Suspense>
            </>
          }
        />

        <Route
          path="/best-salon-software-india"
          element={
            <>
              <SEOHead
                title="Best Salon Software in India 2026"
                description="Compare the best salon management software in India. AyeSalon offers GST billing, WhatsApp reminders, online booking, staff management and client CRM for Indian salons."
                canonical="https://ayesalon.com/best-salon-software-india"
                ogImage="/og-features.jpg"
                keywords="best salon software india, best salon management software india 2026, top salon software india, salon software comparison india"
              />
              <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/best-salon-software-india", [
                { name: "Home", url: "/" },
                { name: "Best Salon Software India", url: "/best-salon-software-india" },
              ])]} />
              <Suspense fallback={<LoadingFallback />}>
                <CompetitorComparison />
              </Suspense>
            </>
          }
        />

        <Route
          path="/success-story/shine-beauty"
          element={
            <>
              <SEOHead
                title={seoConfig["success-story"]?.title}
                description={seoConfig["success-story"]?.description}
                canonical={seoConfig["success-story"]?.canonical}
                ogImage={seoConfig["success-story"]?.ogImage}
                keywords={seoConfig["success-story"]?.keywords}
              />
              <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/success-story/shine-beauty", [
                { name: "Home", url: "/" },
                { name: "Success Stories", url: "/testimonials" },
                { name: "Shine Beauty", url: "/success-story/shine-beauty" },
              ])]} />
              <Suspense fallback={<LoadingFallback />}>
                <SuccessStory />
              </Suspense>
            </>
          }
        />

        {/* ✅ How-To Guides (Phase 5 Week 2) */}
        <Route path="/guides" element={<>
          <SEOHead title={seoConfig["guides-hub"]?.title} description={seoConfig["guides-hub"]?.description} canonical={seoConfig["guides-hub"]?.canonical} ogImage={seoConfig["guides-hub"]?.ogImage} keywords={seoConfig["guides-hub"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/guides", [{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }])]} />
          <Suspense fallback={<LoadingFallback />}><GuidesHub /></Suspense>
        </> } />
        <Route path="/guides/setup-5-step" element={<>
          <SEOHead title={seoConfig["guide-setup-5step"]?.title} description={seoConfig["guide-setup-5step"]?.description} canonical={seoConfig["guide-setup-5step"]?.canonical} keywords={seoConfig["guide-setup-5step"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/guides/setup-5-step", [{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }, { name: "5-Step Setup", url: "/guides/setup-5-step" }])]} />
          <Suspense fallback={<LoadingFallback />}><Setup5StepGuide /></Suspense>
        </> } />
        <Route path="/guides/reduce-no-shows" element={<>
          <SEOHead title={seoConfig["guide-reduce-no-shows"]?.title} description={seoConfig["guide-reduce-no-shows"]?.description} canonical={seoConfig["guide-reduce-no-shows"]?.canonical} keywords={seoConfig["guide-reduce-no-shows"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/guides/reduce-no-shows", [{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }, { name: "Reduce No-Shows", url: "/guides/reduce-no-shows" }])]} />
          <Suspense fallback={<LoadingFallback />}><ReduceNoShowsGuide /></Suspense>
        </> } />
        <Route path="/guides/save-40-hours" element={<>
          <SEOHead title={seoConfig["guide-save-40-hours"]?.title} description={seoConfig["guide-save-40-hours"]?.description} canonical={seoConfig["guide-save-40-hours"]?.canonical} keywords={seoConfig["guide-save-40-hours"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/guides/save-40-hours", [{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }, { name: "Save 40 Hours", url: "/guides/save-40-hours" }])]} />
          <Suspense fallback={<LoadingFallback />}><Save40HoursGuide /></Suspense>
        </> } />
        <Route path="/guides/commission-system" element={<>
          <SEOHead title={seoConfig["guide-commission"]?.title} description={seoConfig["guide-commission"]?.description} canonical={seoConfig["guide-commission"]?.canonical} keywords={seoConfig["guide-commission"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/guides/commission-system", [{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }, { name: "Commission System", url: "/guides/commission-system" }])]} />
          <Suspense fallback={<LoadingFallback />}><CommissionSystemGuide /></Suspense>
        </> } />
        <Route path="/guides/mobile-booking" element={<>
          <SEOHead title={seoConfig["guide-mobile-booking"]?.title} description={seoConfig["guide-mobile-booking"]?.description} canonical={seoConfig["guide-mobile-booking"]?.canonical} keywords={seoConfig["guide-mobile-booking"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/guides/mobile-booking", [{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }, { name: "Mobile Booking", url: "/guides/mobile-booking" }])]} />
          <Suspense fallback={<LoadingFallback />}><MobileBookingOptimizationGuide /></Suspense>
        </> } />
        <Route path="/guides/inventory-tracking" element={<>
          <SEOHead title={seoConfig["guide-inventory"]?.title} description={seoConfig["guide-inventory"]?.description} canonical={seoConfig["guide-inventory"]?.canonical} keywords={seoConfig["guide-inventory"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/guides/inventory-tracking", [{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }, { name: "Inventory Tracking", url: "/guides/inventory-tracking" }])]} />
          <Suspense fallback={<LoadingFallback />}><InventoryTrackingGuide /></Suspense>
        </> } />
        <Route path="/guides/whatsapp-marketing" element={<>
          <SEOHead title={seoConfig["guide-whatsapp"]?.title} description={seoConfig["guide-whatsapp"]?.description} canonical={seoConfig["guide-whatsapp"]?.canonical} keywords={seoConfig["guide-whatsapp"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/guides/whatsapp-marketing", [{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }, { name: "WhatsApp Marketing", url: "/guides/whatsapp-marketing" }])]} />
          <Suspense fallback={<LoadingFallback />}><WhatsAppMarketingGuide /></Suspense>
        </> } />
        <Route path="/guides/pricing-strategy" element={<>
          <SEOHead title={seoConfig["guide-pricing"]?.title} description={seoConfig["guide-pricing"]?.description} canonical={seoConfig["guide-pricing"]?.canonical} keywords={seoConfig["guide-pricing"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/guides/pricing-strategy", [{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }, { name: "Pricing Strategy", url: "/guides/pricing-strategy" }])]} />
          <Suspense fallback={<LoadingFallback />}><PricingStrategyGuide /></Suspense>
        </> } />

        {/* ✅ Phase 5 Week 3 Content Pages */}
        <Route path="/testimonials" element={<>
          <SEOHead title={seoConfig.testimonials?.title} description={seoConfig.testimonials?.description} canonical={seoConfig.testimonials?.canonical} ogImage={seoConfig.testimonials?.ogImage} keywords={seoConfig.testimonials?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/testimonials", [{ name: "Home", url: "/" }, { name: "Testimonials", url: "/testimonials" }])]} />
          <Suspense fallback={<LoadingFallback />}><TestimonialsHub /></Suspense>
        </> } />
        <Route path="/roi-calculator" element={<>
          <SEOHead title={seoConfig["roi-calculator"]?.title} description={seoConfig["roi-calculator"]?.description} canonical={seoConfig["roi-calculator"]?.canonical} ogImage={seoConfig["roi-calculator"]?.ogImage} keywords={seoConfig["roi-calculator"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/roi-calculator", [{ name: "Home", url: "/" }, { name: "ROI Calculator", url: "/roi-calculator" }])]} />
          <Suspense fallback={<LoadingFallback />}><ROICalculator /></Suspense>
        </> } />
        <Route path="/mobile-app" element={<>
          <SEOHead title={seoConfig["mobile-app"]?.title} description={seoConfig["mobile-app"]?.description} canonical={seoConfig["mobile-app"]?.canonical} ogImage={seoConfig["mobile-app"]?.ogImage} keywords={seoConfig["mobile-app"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/mobile-app", [{ name: "Home", url: "/" }, { name: "Mobile App", url: "/mobile-app" }])]} />
          <Suspense fallback={<LoadingFallback />}><MobileApp /></Suspense>
        </> } />

        <Route path="/use-cases" element={<>
          <SEOHead title={seoConfig["use-cases"]?.title} description={seoConfig["use-cases"]?.description} canonical={seoConfig["use-cases"]?.canonical} ogImage={seoConfig["use-cases"]?.ogImage} keywords={seoConfig["use-cases"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/use-cases", [{ name: "Home", url: "/" }, { name: "Use Cases", url: "/use-cases" }])]} />
          <Suspense fallback={<LoadingFallback />}><UseCasesHub /></Suspense>
        </> } />

        {/* ✅ Phase 6 Session 1 - Detailed Use-Case Pages */}
        <Route path="/use-cases/solo-stylist" element={<>
          <SEOHead title={seoConfig["use-cases-solo-stylist"]?.title} description={seoConfig["use-cases-solo-stylist"]?.description} canonical={seoConfig["use-cases-solo-stylist"]?.canonical} ogImage={seoConfig["use-cases-solo-stylist"]?.ogImage} keywords={seoConfig["use-cases-solo-stylist"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/use-cases/solo-stylist", [{ name: "Home", url: "/" }, { name: "Use Cases", url: "/use-cases" }, { name: "Solo Stylist", url: "/use-cases/solo-stylist" }])]} />
          <Suspense fallback={<LoadingFallback />}><SoloStylist /></Suspense>
        </> } />
        <Route path="/use-cases/small-team" element={<>
          <SEOHead title={seoConfig["use-cases-small-team"]?.title} description={seoConfig["use-cases-small-team"]?.description} canonical={seoConfig["use-cases-small-team"]?.canonical} ogImage={seoConfig["use-cases-small-team"]?.ogImage} keywords={seoConfig["use-cases-small-team"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/use-cases/small-team", [{ name: "Home", url: "/" }, { name: "Use Cases", url: "/use-cases" }, { name: "Small Team", url: "/use-cases/small-team" }])]} />
          <Suspense fallback={<LoadingFallback />}><SmallTeam /></Suspense>
        </> } />
        <Route path="/use-cases/medium-salon" element={<>
          <SEOHead title={seoConfig["use-cases-medium-salon"]?.title} description={seoConfig["use-cases-medium-salon"]?.description} canonical={seoConfig["use-cases-medium-salon"]?.canonical} ogImage={seoConfig["use-cases-medium-salon"]?.ogImage} keywords={seoConfig["use-cases-medium-salon"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/use-cases/medium-salon", [{ name: "Home", url: "/" }, { name: "Use Cases", url: "/use-cases" }, { name: "Medium Salon", url: "/use-cases/medium-salon" }])]} />
          <Suspense fallback={<LoadingFallback />}><MediumSalon /></Suspense>
        </> } />
        <Route path="/use-cases/multi-location" element={<>
          <SEOHead title={seoConfig["use-cases-multi-location"]?.title} description={seoConfig["use-cases-multi-location"]?.description} canonical={seoConfig["use-cases-multi-location"]?.canonical} ogImage={seoConfig["use-cases-multi-location"]?.ogImage} keywords={seoConfig["use-cases-multi-location"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/use-cases/multi-location", [{ name: "Home", url: "/" }, { name: "Use Cases", url: "/use-cases" }, { name: "Multi-Location", url: "/use-cases/multi-location" }])]} />
          <Suspense fallback={<LoadingFallback />}><MultiLocation /></Suspense>
        </> } />
        <Route path="/use-cases/spa-wellness" element={<>
          <SEOHead title={seoConfig["use-cases-spa-wellness"]?.title} description={seoConfig["use-cases-spa-wellness"]?.description} canonical={seoConfig["use-cases-spa-wellness"]?.canonical} ogImage={seoConfig["use-cases-spa-wellness"]?.ogImage} keywords={seoConfig["use-cases-spa-wellness"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/use-cases/spa-wellness", [{ name: "Home", url: "/" }, { name: "Use Cases", url: "/use-cases" }, { name: "Spa & Wellness", url: "/use-cases/spa-wellness" }])]} />
          <Suspense fallback={<LoadingFallback />}><SpaWellness /></Suspense>
        </> } />

        {/* ✅ Phase 6 Session 2 - Video Testimonials & Analytics */}
        <Route path="/videos" element={<>
          <SEOHead title={seoConfig.videos?.title} description={seoConfig.videos?.description} canonical={seoConfig.videos?.canonical} ogImage={seoConfig.videos?.ogImage} keywords={seoConfig.videos?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/videos", [{ name: "Home", url: "/" }, { name: "Video Testimonials", url: "/videos" }])]} />
          <Suspense fallback={<LoadingFallback />}><VideoTestimonials /></Suspense>
        </> } />
        <Route path="/dashboard/analytics" element={<><Helmet><meta name="robots" content="noindex, nofollow" /></Helmet><Suspense fallback={<LoadingFallback />}><AnalyticsDashboard /></Suspense></> } />

        {/* ✅ Phase 6 Session 3 - Lead Management */}
        <Route path="/dashboard/leads" element={<><Helmet><meta name="robots" content="noindex, nofollow" /></Helmet><Suspense fallback={<LoadingFallback />}><LeadManagementDashboard /></Suspense></> } />

        {/* ✅ Phase 5 Session 4 - Blog (Batch 1 How-To Articles) */}
        <Route path="/blog" element={<>
          <SEOHead title={seoConfig.blog?.title} description={seoConfig.blog?.description} canonical={seoConfig.blog?.canonical} ogImage={seoConfig.blog?.ogImage} keywords={seoConfig.blog?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/blog", [{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }])]} />
          <Suspense fallback={<LoadingFallback />}><Blog /></Suspense>
        </> } />
        <Route path="/blog/:slug" element={<><Suspense fallback={<LoadingFallback />}><BlogArticle /></Suspense></> } />

        {/* ✅ Feature deep-dive animated videos */}
        <Route path="/feature-videos" element={<>
          <SEOHead title={seoConfig["feature-videos"]?.title} description={seoConfig["feature-videos"]?.description} canonical={seoConfig["feature-videos"]?.canonical} ogImage={seoConfig["feature-videos"]?.ogImage} keywords={seoConfig["feature-videos"]?.keywords} />
          <SchemaMarkup schemas={[organizationSchema, breadcrumbSchema("/feature-videos", [{ name: "Home", url: "/" }, { name: "Feature Videos", url: "/feature-videos" }])]} />
          <Suspense fallback={<LoadingFallback />}><FeatureVideos /></Suspense>
        </>} />

        {/* ✅ City Landing Pages (Phase 5.5 - GEO Targeting) */}
        <Route path="/salon-software-bangalore" element={<><Suspense fallback={<LoadingFallback />}><BangalorePage /></Suspense></> } />
        <Route path="/salon-software-delhi" element={<><Suspense fallback={<LoadingFallback />}><DelhiPage /></Suspense></> } />
        <Route path="/salon-software-hyderabad" element={<><Suspense fallback={<LoadingFallback />}><HyderabadPage /></Suspense></> } />
        <Route path="/salon-software-chennai" element={<><Suspense fallback={<LoadingFallback />}><ChennaiPage /></Suspense></> } />
        <Route path="/salon-software-pune" element={<><Suspense fallback={<LoadingFallback />}><PunePage /></Suspense></> } />
        <Route path="/salon-software-kolkata" element={<><Suspense fallback={<LoadingFallback />}><KolkataPage /></Suspense></> } />
        <Route path="/salon-software-ahmedabad" element={<><Suspense fallback={<LoadingFallback />}><AhmedabadPage /></Suspense></> } />
        <Route path="/salon-software-mumbai" element={<><Suspense fallback={<LoadingFallback />}><MumbaiPage /></Suspense></> } />
        {/* Public partner booking page */}
        <Route path="/partner/:slug" element={<Suspense fallback={<LoadingFallback />}><SalonPublicPage /></Suspense>} />
        {/* Embeddable booking widget — chromeless, meant to be iframed on a salon's own website */}
        <Route path="/embed/:slug" element={<Suspense fallback={<LoadingFallback />}><EmbedBookingPage /></Suspense>} />

        {/* 404 NOT FOUND */}
        <Route path="/lp/revenue" element={<Suspense fallback={<LoadingFallback />}><GrowRevenueLanding /></Suspense>} />
        <Route path="/lp/thank-you" element={<Suspense fallback={<LoadingFallback />}><ThankYouLanding /></Suspense>} />
        <Route path="/lp/software-only" element={<Suspense fallback={<LoadingFallback />}><SoftwareOnlyLanding /></Suspense>} />
        <Route path="*" element={<Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>} />
    </Routes>
    </>
  );
}
